let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4;
import * as styledComponents from 'styled-components';
import { BATTLESHIP, CALYPSO, CALYPSO_LIGHT, CALYPSO_DARK, CANDY_APPLE, EERIE, FLAT_EARTH, FLINT, GREAT_WHITE, GYPSUM, HEFFALUMP, KOALA, LORAX, MONOLITH, OBSIDIAN, OLAF, PANTERA, SLINKY, SORBET } from 'HubStyleTokens/colors';
import { GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_MEDIUM, GRID_BREAKPOINT_LARGE, GRID_BREAKPOINT_EXTRA_LARGE } from 'HubStyleTokens/sizes';
import { SLIDEIN_MODAL_TRANSITION_TIMING } from 'HubStyleTokens/times';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import debounce from 'react-utils/debounce';
import produce from 'immer';
import { useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
export const theme = {
  activeTheme: 'light',
  colors: {
    BATTLESHIP,
    CALYPSO,
    CALYPSO_LIGHT,
    CALYPSO_DARK,
    CANDY_APPLE,
    EERIE,
    FLAT_EARTH,
    FLINT,
    GREAT_WHITE,
    GYPSUM,
    HEFFALUMP,
    KOALA,
    LORAX,
    MONOLITH,
    OBSIDIAN,
    OLAF,
    PANTERA,
    SLINKY,
    SORBET,
    HIGH_CONTRAST_PRIMARY: '#1d0cff',
    HIGH_CONTRAST_LINK: '#0000c5',
    HIGH_CONTRAST_HOVER: '#cee2ff'
  },
  sizes: {
    GRID_BREAKPOINT_SMALL,
    GRID_BREAKPOINT_MEDIUM,
    GRID_BREAKPOINT_LARGE,
    GRID_BREAKPOINT_EXTRA_LARGE,
    PUBLIC_NAV_FULL_HEIGHT: '80px',
    PUBLIC_NAV_REDUCED_HEIGHT: '60px',
    PUBLIC_PAGE_WIDTH: '1080px'
  },
  times: {
    SLIDEIN_MODAL_TRANSITION_TIMING
  }
};
const {
  default: styled,
  css,
  ThemeProvider: StyledComponentsThemeProvider,
  createGlobalStyle,
  keyframes,
  ThemeConsumer
} = styledComponents;
export const useThemeStore = create()(persist(set => ({
  theme: 'light',
  setTheme: newTheme => set({
    theme: newTheme
  })
}), {
  name: 'academy:theme'
}));
const ThemeProvider = ({
  children
}) => {
  const {
    theme: themeName
  } = useThemeStore();
  const dynamicTheme = useMemo(() => Object.assign({}, theme, {
    activeTheme: themeName
  }), [themeName]);
  return /*#__PURE__*/_jsx(StyledComponentsThemeProvider, {
    theme: dynamicTheme,
    children: children
  });
};
export { css, createGlobalStyle, keyframes, ThemeProvider, ThemeConsumer };
export default styled;
const devices = {
  mobile: `(max-width: ${GRID_BREAKPOINT_SMALL})`,
  tablet: `(max-width: ${GRID_BREAKPOINT_MEDIUM})`,
  laptop: `(max-width: ${GRID_BREAKPOINT_LARGE})`,
  desktop: `(min-width: ${GRID_BREAKPOINT_EXTRA_LARGE})`,
  notMobile: `(min-width: ${GRID_BREAKPOINT_SMALL})`,
  notTablet: `(min-width: ${GRID_BREAKPOINT_MEDIUM})`,
  notLaptop: `(min-width: ${GRID_BREAKPOINT_LARGE})`,
  mobileNavbar: `(max-width: 1059px)`,
  desktopNavbar: `(min-width: 1060px)`,
  touchscreen: '(hover: none) and (pointer: coarse)'
};
export const mediaQuery = (device, styles) => {
  return css(_t || (_t = _`
    @media screen and ${0} {
      ${0}
    }
  `), devices[device], styles);
};
const mediaMatchers = {
  mobile: matchMedia(devices.mobile),
  tablet: matchMedia(devices.tablet),
  laptop: matchMedia(devices.laptop),
  desktop: matchMedia(devices.desktop),
  notMobile: matchMedia(devices.notMobile),
  notTablet: matchMedia(devices.notTablet),
  notLaptop: matchMedia(devices.notLaptop),
  mobileNavbar: matchMedia(devices.mobileNavbar),
  desktopNavbar: matchMedia(devices.desktopNavbar),
  touchscreen: matchMedia(devices.touchscreen)
};
const matchMediaSizes = () => ({
  isMobile: mediaMatchers.mobile.matches,
  isTablet: mediaMatchers.tablet.matches,
  isLaptop: mediaMatchers.laptop.matches,
  isDesktop: mediaMatchers.desktop.matches,
  isNotMobile: mediaMatchers.notMobile.matches,
  isNotTablet: mediaMatchers.notTablet.matches,
  isNotLaptop: mediaMatchers.notLaptop.matches,
  isMobileNavbar: mediaMatchers.mobileNavbar.matches,
  isDesktopNavbar: mediaMatchers.desktopNavbar.matches
});
const useMediaQueryInternal = create(set => Object.assign({}, matchMediaSizes(), {
  isTouchscreen: mediaMatchers.touchscreen.matches,
  setMatches: debounce(() => set(Object.assign({}, matchMediaSizes())), 50)
}));
export const useMediaQuery = matchDevice => {
  const result = useMediaQueryInternal();
  return produce(result, draft => {
    if (matchDevice) {
      draft.matches = mediaMatchers[matchDevice].matches;
    }
  });
};
const initWindowWatcher = (win = window) => {
  win.addEventListener('resize', () => {
    useMediaQueryInternal.getState().setMatches();
  });
};
initWindowWatcher();
export const academyPageFullWidth = css(_t2 || (_t2 = _`
  width: 100%;
  max-width: 1376px;
  margin: 0 auto;
`));
export const academyResultsBar = css(_t3 || (_t3 = _`
  width: 100%;
  height: 40px;
  background-color: ${0};
  ${0}
`), theme.colors.GYPSUM, mediaQuery('notMobile', css(_t4 || (_t4 = _`
      background-color: ${0};
    `), theme.colors.CALYPSO_DARK)));